import type { ReactNode } from 'react';

import type { WorkSurface } from '@/models/analytics/features/work';
import { ProseDensityProvider } from '@heidi/answer-kit';

import type { SubmitProjectTurnArgs } from '@heidi/agents/data';
import {
  ProjectChat,
  type ProjectChatProps,
  type ProvisionalSubmitOutcome,
} from './ProjectChat';
import type { ProjectsComposerSessionAppearance } from './ProjectsLandingComposer';

export type { ProvisionalSubmitOutcome, SubmitProjectTurnArgs };

export interface ProjectRunChatBodyProps {
  /** Includes `undefined`: one element spans the landing → project flip. */
  projectId: string | undefined;
  projectTitle: string;
  emptyState?: ReactNode;
  prefilledPrompt?: string;
  provisionalFirstTurnText?: string | null;
  provisionalIsSubmitting?: boolean;
  /** Hardcoded opening assistant turn carried in from a guided create, or
   *  (with `openingAssistantMessageFeedback`) New Chat's routed greeting. */
  openingAssistantMessage?: string;
  /** See `ProjectChatProps['openingAssistantMessageFeedback']`. */
  openingAssistantMessageFeedback?: boolean;
  analyticsSurface?: WorkSurface;
  composerBackdrop?: ReactNode;
  /** Page-owned control rendered in the composer toolbar (Work home Auto/Evidence). */
  composerToolbarLeading?: ReactNode;
  /** Host-owned Fast/Normal control for the composer + menu. */
  composerSpeedMenu?: ProjectChatProps['composerSpeedMenu'];
  composerMode?: ProjectChatProps['composerMode'];
  onComposerModeChange?: ProjectChatProps['onComposerModeChange'];
  composerSessionAppearance?: ProjectsComposerSessionAppearance;
  composerAutoFocus?: boolean;
  onComposerFocusChange?: (focused: boolean) => void;
  onComposerDraftEmptyChange?: (empty: boolean) => void;
  composerExpanded?: boolean;
  onComposerExpandedChange?: (expanded: boolean) => void;
  centerProvisionalContent?: boolean;
  onHasQueuedTurnsChange?: (hasQueuedTurns: boolean) => void;
  /** Required, not defaulted: the id can be absent on any render and a stand-in
   *  handler would swallow that send. */
  onProvisionalSubmit: (args: SubmitProjectTurnArgs) => Promise<ProvisionalSubmitOutcome>;
  /** Restricted Work-home chats (same thread as in-session) keep the Figma lock shell. */
  lockComposerContext?: boolean;
  composerLayout?: ProjectChatProps['composerLayout'];
  toolbarSourcesSlot?: ReactNode;
  /** Landing only: the host's draft, shared with whatever it swaps this chat for.
   *  A bound chat owns its own draft. */
  landingDraft?: ProjectChatProps['hostDraft'];
  /** Shell content column. The full-screen drop fills this and leaves the ribbon out. */
  fileDropContent?: HTMLElement | null;
}

/**
 * The ONE `ProjectChat` element position on the Work route. Never key it by
 * project or run id: it must keep its position and type across the URL flip or
 * the first send remounts it.
 */
export const ProjectRunChatBody = ({
  projectId,
  projectTitle,
  emptyState,
  prefilledPrompt,
  provisionalFirstTurnText = null,
  provisionalIsSubmitting = false,
  openingAssistantMessage,
  openingAssistantMessageFeedback,
  analyticsSurface,
  composerBackdrop,
  composerToolbarLeading,
  composerSpeedMenu,
  composerMode,
  onComposerModeChange,
  composerSessionAppearance,
  composerAutoFocus,
  onComposerFocusChange,
  onComposerDraftEmptyChange,
  composerExpanded,
  onComposerExpandedChange,
  centerProvisionalContent,
  onHasQueuedTurnsChange,
  onProvisionalSubmit,
  lockComposerContext = false,
  composerLayout,
  toolbarSourcesSlot,
  landingDraft,
  fileDropContent,
}: ProjectRunChatBodyProps) => {
  const chatProps: ProjectChatProps = projectId
    ? {
        projectId,
        projectTitle,
        lockComposerContext,
        prefilledPrompt,
        openingAssistantMessage,
        openingAssistantMessageFeedback,
        analyticsSurface,
        onHasQueuedTurnsChange,
        // Still carried once bound: the in-place first send flips the id
        // mid-flight, so these belong to the create the host is driving.
        provisionalFirstTurnText,
        provisionalIsSubmitting,
        composerBackdrop,
        composerToolbarLeading,
        composerSpeedMenu,
        composerMode,
        onComposerModeChange,
        composerSessionAppearance,
        composerLayout,
        toolbarSourcesSlot,
        composerAutoFocus,
        onComposerFocusChange,
        onComposerDraftEmptyChange,
        composerExpanded,
        onComposerExpandedChange,
        centerProvisionalContent,
        fileDropScope: 'window',
        fileDropContent,
      }
    : {
        projectId: undefined,
        projectTitle: '',
        emptyState,
        prefilledPrompt,
        openingAssistantMessage,
        openingAssistantMessageFeedback,
        analyticsSurface,
        onHasQueuedTurnsChange,
        provisionalFirstTurnText,
        provisionalIsSubmitting,
        composerBackdrop,
        composerToolbarLeading,
        composerSpeedMenu,
        composerMode,
        onComposerModeChange,
        composerSessionAppearance,
        composerLayout,
        toolbarSourcesSlot,
        composerAutoFocus,
        onComposerFocusChange,
        onComposerDraftEmptyChange,
        composerExpanded,
        onComposerExpandedChange,
        centerProvisionalContent,
        onProvisionalSubmit,
        hostDraft: landingDraft,
        fileDropScope: 'window',
        fileDropContent,
      };

  // Full-screen Work is a primary reading surface, so it publishes
  // `comfortable` for the whole subtree. Publishing here rather than letting
  // each consumer fall back is what keeps one surface on one density: the
  // prose defaulted to `comfortable` while `ProjectTurnTimeline` fell back to
  // `compact`, so the thread drew compact rungs around comfortable body copy.
  return (
    <ProseDensityProvider density="comfortable">
      <ProjectChat {...chatProps} />
    </ProseDensityProvider>
  );
};
